/**
 * Reusable Packing-List action button + dropdown — generates one of the
 * three warehouse documents (Picking / Packing / Delivery Note) for any
 * sale, draft, quotation or invoice and opens the resulting PDF in a new
 * tab via blob URL.
 *
 * Usage:
 *   <PackingListMenu transactionId={tx.id} compact />
 */
import React, { useState } from 'react';
import { Truck, ClipboardCheck, FileSignature, Loader2, PackageCheck } from 'lucide-react';
import { Button } from '../ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { salesAPI } from '../../lib/api';
import { toast } from 'sonner';

const VARIANTS = [
    {
        kind: 'picking',
        label: 'Bon de Préparation',
        sub: 'Warehouse picking sheet · checkboxes',
        icon: ClipboardCheck,
    },
    {
        kind: 'packing',
        label: 'Bon de Colis',
        sub: 'Customer packing list · in shipment',
        icon: PackageCheck,
    },
    {
        kind: 'delivery',
        label: 'Bon de Livraison',
        sub: 'Delivery note · with signature area',
        icon: FileSignature,
    },
];

const PackingListMenu = ({ transactionId, compact = false, testIdPrefix = 'pkg' }) => {
    const [busyKind, setBusyKind] = useState(null);

    const open = async (kind) => {
        if (!transactionId) return;
        setBusyKind(kind);
        try {
            const { data } = await salesAPI.packingListPdf(transactionId, { kind });
            const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
            window.open(url, '_blank');
            setTimeout(() => URL.revokeObjectURL(url), 30000);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to generate document');
        } finally {
            setBusyKind(null);
        }
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="ghost"
                    size={compact ? 'icon' : 'sm'}
                    title="Generate packing / delivery document"
                    data-testid={`${testIdPrefix}-trigger-${transactionId}`}
                    className={compact ? 'text-gray-400 hover:text-[#C08478]' : 'border border-[#E5E5E5]'}
                >
                    {busyKind ? <Loader2 size={16} className="animate-spin" /> : <Truck size={compact ? 16 : 14} className={compact ? '' : 'mr-2'} />}
                    {!compact && <span>Packing</span>}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
                <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-gray-400">
                    Generate document
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {VARIANTS.map(({ kind, label, sub, icon: Icon }) => (
                    <DropdownMenuItem
                        key={kind}
                        disabled={!!busyKind}
                        onClick={() => open(kind)}
                        data-testid={`${testIdPrefix}-${kind}-${transactionId}`}
                        className="flex items-start gap-3 py-2 cursor-pointer"
                    >
                        <Icon size={18} className="text-[#C08478] mt-0.5 shrink-0" />
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium">{label}</div>
                            <div className="text-[11px] text-gray-500 leading-tight">{sub}</div>
                        </div>
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

export default PackingListMenu;
